import { Sparkles, Mic, Bot, FileDown, BarChart3, Plug, SlidersHorizontal, ShieldCheck, Users, KeyRound, Zap, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import EarlyAccessCta from "@/components/site/EarlyAccessCta";
import DemoModal from "@/components/site/DemoModal";
import StickyDemoCta from "@/components/site/StickyDemoCta";
import { Link } from "react-router-dom";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/p43aljnc_live%20scoring.png";
const MEETING_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/ltvcuu1f_New%20Qualification.png";
const MANAGER_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/ouxj5bc1_new%20team%20insights.png";

const sections = [
    {
        eyebrow: "Live Call Scoring",
        title: "A qualification score that updates while you talk.",
        body: "PipeGuide scores every minute of your call against BANT, MEDDIC, SPIN or your custom framework — so reps know exactly where the deal is, and what's missing, before they hang up.",
        bullets: [
            "Real-time score updates every 15 seconds",
            "Per-category breakdowns with confidence levels",
            "Talk ratio, sentiment & topic detection",
            "Manual override (Pro: 3/meeting, Business: 5/meeting)",
        ],
        img: HERO_IMG,
        dark: false,
    },
    {
        eyebrow: "AI-Suggested Questions",
        title: "Reps never get stuck on what to ask next.",
        body: "Context-aware probing questions adapt as the conversation moves. Drawn from your framework's gaps and the prospect's last answer — not a static script.",
        bullets: [
            "3 fresh suggestions every 15s",
            "One-tap mark as 'asked'",
            "Follow-up depth tuned to seniority",
            "Custom framework? Custom suggestions.",
        ],
        img: MEETING_IMG,
        dark: false,
    },
    {
        eyebrow: "Manager Insights",
        title: "Walk into 1:1s already knowing what to coach.",
        body: "A weekly AI digest of leaderboard movement, category breakdowns, and per-rep coaching points. Built for the manager who has eight reps and 30 minutes a week.",
        bullets: [
            "Monday 6am digest email",
            "AI-generated coaching points per rep",
            "Praise highlights so 1:1s start positive",
            "Trend tracking (week-on-week, MoM)",
        ],
        img: MANAGER_IMG,
        dark: false,
    },
];

const cards = [
    { icon: Bot, title: "Meeting Bot", body: "Invite PipeGuide to Zoom, Teams or Meet. It joins, transcribes and analyses. Reps don't need the app open." },
    { icon: FileDown, title: "PDF Summaries", body: "One-click export with scores, key moments and next steps. Brand it on Business." },
    { icon: Plug, title: "CRM Push", body: "Native integrations with HubSpot, Pipedrive and Zoho. Score, summary and transcript on the deal." },
    { icon: SlidersHorizontal, title: "Custom Frameworks", body: "Build your own qualification model with custom categories and weightings." },
    { icon: ShieldCheck, title: "Multi-Tenant", body: "Each org fully isolated. JWT auth, encrypted connections, no data crossover." },
    { icon: KeyRound, title: "Google SSO", body: "One-click sign-in with Google Workspace. No more password sprawl." },
    { icon: Users, title: "Roles & Permissions", body: "Admin, Manager and Sales Rep roles with department-scoped data." },
    { icon: Zap, title: "Built for Speed", body: "Sub-second score updates. No noticeable lag during the call." },
];

export default function Features() {
    return (
        <div data-testid="features-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Features</p>
                        <h1 className="mt-3 font-display text-5xl sm:text-6xl font-bold tracking-tight text-zinc-900">
                            Built for the messy reality of live discovery.
                        </h1>
                        <p className="mt-5 text-lg text-zinc-600">
                            From the first discovery call to the CRM update — PipeGuide is the layer that makes your qualification calls sharper.
                        </p>
                    </div>
                </div>
            </section>

            {/* Big sections */}
            {sections.map((s, i) => (
                <section
                    key={i}
                    data-testid={`feature-section-${i}`}
                    className={`${s.dark ? "bg-zinc-950 text-white" : "bg-white text-zinc-900"} ${i > 0 ? "border-t border-zinc-200" : ""} ${s.dark ? "border-zinc-900" : ""}`}
                >
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                            <div>
                                <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${s.dark ? "text-brand-accent" : "text-brand-primary"}`}>{s.eyebrow}</p>
                                <h2 className={`mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight ${s.dark ? "text-white" : "text-zinc-900"}`}>
                                    {s.title}
                                </h2>
                                <p className={`mt-4 text-lg ${s.dark ? "text-zinc-400" : "text-zinc-600"} leading-relaxed`}>{s.body}</p>
                                <ul className="mt-8 space-y-3">
                                    {s.bullets.map((b, k) => (
                                        <li key={k} className={`flex items-start gap-3 text-sm ${s.dark ? "text-zinc-200" : "text-zinc-700"}`}>
                                            <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${s.dark ? "bg-brand-accent" : "bg-brand-primary"}`} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <img
                                    src={s.img}
                                    alt={s.title}
                                    data-testid={`feature-section-img-${i}`}
                                    className={`w-full rounded-2xl border ${s.dark ? "border-zinc-800" : "border-zinc-200"} shadow-2xl`}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* Capability grid */}
            <section className="bg-zinc-50 border-t border-zinc-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Platform</p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">More capabilities</h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {cards.map((c) => (
                            <div key={c.title} data-testid={`features-card-${c.title.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-zinc-200 bg-white p-6 hover:border-brand-accent-muted hover:shadow-md transition-all">
                                <c.icon className="h-5 w-5 text-brand-primary" />
                                <h3 className="mt-4 font-display text-lg font-semibold text-zinc-900">{c.title}</h3>
                                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{c.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white relative overflow-hidden border-t border-zinc-200">
                <div className="absolute inset-0 bg-grid-light opacity-60" aria-hidden />
                <div className="absolute inset-x-0 top-0 h-[600px] bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(37,99,235,0.10),transparent_70%)]" aria-hidden />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-20 md:py-28 text-center">
                    <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        See it on your next call.
                    </h2>
                    <p className="mt-4 text-lg text-zinc-600 max-w-xl mx-auto">
                        Spin up a free account in under two minutes. Five live calls per month, no card required.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                        <EarlyAccessCta data-testid="features-cta-start" className="bg-brand-primary hover:bg-brand-hover text-white h-12 px-8 glow-blue" />
                        <Button asChild variant="outline" data-testid="features-cta-pricing" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-8">
                            <Link to="/pricing">See pricing</Link>
                        </Button>
                        <DemoModal trigger={<Button variant="outline" data-testid="features-cta-demo" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-8">Book a demo</Button>} />
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
